/** Portrait table layout helpers for native PDF export. */

import {
  PDF_PORTRAIT_CONTENT_HEIGHT_PT,
  PDF_PORTRAIT_USABLE_WIDTH_PT,
} from './protocolPdfLayout';
import {
  PORTRAIT_TABLE_HEADER_HEIGHT_PT,
} from './pdfTablePagination';
import { sanitizePdfPt } from './pdfFontSetup';

const CELL_PAD_PT = 8;
/** Fewer columns per part = wider, less “compressed” cells. */
const MAX_PORTRAIT_COLS_PER_TABLE = 6;

export const PORTRAIT_ROW_PAGE_BUDGET_PT =
  PDF_PORTRAIT_CONTENT_HEIGHT_PT - PORTRAIT_TABLE_HEADER_HEIGHT_PT - 24;

function estimateCharsPerLine(colWidthPt, fontSizePt) {
  const inner = Math.max(6, Number(colWidthPt) - CELL_PAD_PT);
  const fs = Math.max(6, Number(fontSizePt) || 11);
  return Math.max(1, Math.floor(inner / (fs * 0.52)));
}

function estimateLinesPerPage(fontSizePt) {
  const fs = Math.max(6, Number(fontSizePt) || 11);
  const lineHeight = fs * 1.35;
  return Math.max(1, Math.floor(PORTRAIT_ROW_PAGE_BUDGET_PT / lineHeight));
}

export function splitPortraitCellText(text, colWidthPt, fontSizePt) {
  const s = String(text ?? '').trim();
  if (!s) return [''];

  const charsPerLine = estimateCharsPerLine(colWidthPt, fontSizePt);
  const linesPerPage = estimateLinesPerPage(fontSizePt);
  const maxChars = Math.max(
    charsPerLine,
    Math.floor(charsPerLine * linesPerPage * 0.4),
  );

  if (s.length <= maxChars) return [s];

  const chunks = [];
  let remaining = s;
  while (remaining.length > 0) {
    if (remaining.length <= maxChars) {
      chunks.push(remaining);
      break;
    }
    let cut = maxChars;
    const space = remaining.lastIndexOf(' ', cut);
    if (space > cut * 0.35) cut = space;
    else {
      const nl = remaining.lastIndexOf('\n', cut);
      if (nl > cut * 0.35) cut = nl;
    }
    chunks.push(remaining.slice(0, cut).trim());
    remaining = remaining.slice(cut).trim();
  }
  return chunks.length ? chunks : [''];
}

export function tableFontSizeForColCount(colCount, fontConfig = null) {
  const base = sanitizePdfPt(fontConfig?.tableCell?.size, 11);
  const n = Number(colCount) || 1;
  // Keep readable sizes — avoid heavily compressed tiny table text.
  if (n > 10) return Math.max(9, base - 1);
  if (n > 6) return Math.max(10, base - 1);
  return base;
}

/**
 * Column widths that sum exactly to usableWidth.
 * 2-col Parameter/Details tables get ~38% / 62%.
 * First column (row labels) gets a larger share for 3+ columns.
 */
export function getPortraitTableColWidths(colCount, usableWidth = PDF_PORTRAIT_USABLE_WIDTH_PT) {
  const n = Math.max(1, Math.min(40, Number(colCount) || 1));
  const total = Math.max(40, Number(usableWidth) || PDF_PORTRAIT_USABLE_WIDTH_PT);

  let widths;
  if (n === 1) {
    widths = [total];
  } else if (n === 2) {
    const first = Math.round(total * 0.38 * 100) / 100;
    widths = [first, Math.round((total - first) * 100) / 100];
  } else {
    // Reserve a readable label column, split the rest evenly.
    const labelShare = Math.min(0.34, Math.max(0.22, 1.15 / n));
    const first = Math.round(total * labelShare * 100) / 100;
    const rest = total - first;
    const other = Math.floor((rest / (n - 1)) * 100) / 100;
    widths = [first, ...Array.from({ length: n - 1 }, () => other)];
    const sum = widths.reduce((a, b) => a + b, 0);
    widths[n - 1] = Math.max(2, Math.round((widths[n - 1] + (total - sum)) * 100) / 100);
  }

  const sum2 = widths.reduce((a, b) => a + b, 0);
  if (sum2 > total + 0.01) {
    const scale = total / sum2;
    for (let i = 0; i < n; i += 1) {
      widths[i] = Math.max(2, Math.round(widths[i] * scale * 100) / 100);
    }
    const sum3 = widths.reduce((a, b) => a + b, 0);
    widths[n - 1] = Math.max(2, Math.round((widths[n - 1] + (total - sum3)) * 100) / 100);
  }
  return widths;
}

function cellPlainText(cell) {
  if (Array.isArray(cell)) return cell.map((r) => r?.text || '').join('').trim();
  return String(cell ?? '').trim();
}

function normalizeCell(cell) {
  if (Array.isArray(cell)) {
    return cell.map((r) => ({
      text: String(r?.text ?? ''),
      bold: Boolean(r?.bold),
      italic: Boolean(r?.italic),
    }));
  }
  const text = String(cell ?? '');
  return text ? [{ text }] : [{ text: ' ' }];
}

/** Pad rows to a uniform column count and normalize cell runs. */
export function normalizePdfTableRows(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];
  const colCount = Math.max(
    1,
    ...rows.map((row) => (Array.isArray(row) ? row.length : 0)),
  );
  return rows.map((row) => {
    const cells = Array.isArray(row) ? row.map(normalizeCell) : [];
    while (cells.length < colCount) cells.push([{ text: ' ' }]);
    return cells.slice(0, colCount);
  });
}

/**
 * Split wide tables into column groups (row header in col 0 repeated).
 * Prevents PDFKit flex overflow on study-parts matrices in Section 4.
 */
export function splitWidePortraitTableRows(rows, maxCols = MAX_PORTRAIT_COLS_PER_TABLE) {
  const normalized = normalizePdfTableRows(rows);
  if (!normalized.length) return [];
  const colCount = normalized[0].length;
  if (colCount <= maxCols) return [normalized];

  const chunks = [];
  let start = 1;
  while (start < colCount) {
    const end = Math.min(colCount, start + maxCols - 1);
    const sliceCols = [0, ...Array.from({ length: end - start }, (_, i) => start + i)];
    chunks.push(
      normalized.map((row) => sliceCols.map((ci) => row[ci] ?? [{ text: ' ' }])),
    );
    start = end;
  }
  return chunks;
}

function expandRowByCellChunks(row, colWidths, fontSize) {
  const chunkSets = row.map((cell, colIdx) =>
    splitPortraitCellText(cellPlainText(cell), colWidths[colIdx], fontSize),
  );
  const partCount = Math.max(1, ...chunkSets.map((c) => c.length));
  if (partCount === 1) return [row];

  const parts = [];
  for (let i = 0; i < partCount; i += 1) {
    const part = row.map((cell, colIdx) => {
      const chunk = chunkSets[colIdx][i] ?? '';
      if (!chunk) return [{ text: ' ' }];
      const runs = normalizeCell(cell);
      if (runs.length === 1 && !runs[0].bold && !runs[0].italic) {
        return [{ text: chunk }];
      }
      return runs.map((r, idx) => (idx === 0 ? { ...r, text: chunk } : { ...r, text: '' }));
    });
    parts.push(part);
  }
  return parts;
}

/** Expand body rows when any cell text would exceed one printable page. */
export function expandPortraitTableRowsForPdf(rows, colWidths, fontSize) {
  const normalized = normalizePdfTableRows(rows);
  if (normalized.length <= 1) return normalized;
  const header = normalized[0];
  const body = normalized.slice(1);
  const expanded = body.flatMap((row) => expandRowByCellChunks(row, colWidths, fontSize));
  return [header, ...expanded];
}
